// React Utils
import { Link } from "react-router-dom";
// Packages components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
// Assets
import bckWoop from "@/assets/Logo-AppWhoop.webp"; // Placeholder image for lazy loading

// Utils
import { scrollToTop, formatFechaHoraEvento } from "@/lib/utilsUI";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";

/**
 * TicketCardEventoPublico
 * Componente para mostrar eventos donde el usuario tiene tickets comprados (agrupados por evento)
 * Este componente se usa para la vista pública de perfiles
 * 
 * @param {Object} props
 * @param {Object} props.evento - Datos del evento
 * @param {string} props.evento.uid - UID del evento
 * @param {string} props.evento.nombre - Nombre del evento
 * @param {string} props.evento.img - URL de la imagen del evento
 * @param {string} props.evento.fechaHoraInicio - Fecha de inicio
 * @param {string} props.evento.fechaHoraFin - Fecha de fin
 * @param {string} props.evento.direccion - Dirección del evento
 * @param {string} props.evento.zonaHoraria - Zona horaria del evento
 * @param {number} props.evento.published - Estado del evento (0=No publicado, 1=Activo, 2=Finalizado, 3=Cancelado)
 */
export default function TicketCardEventoPublico({ ticket }) {
  console.log("Rendering TicketCardEventoPublico with evento:", ticket);
  
  // Formatear fecha de inicio del evento usando UTC
  const formatDatetime = ticket?.fechaHoraInicio
    ? formatFechaHoraEvento(
        parseUTCDate(ticket.fechaHoraInicio, ticket.zonaHoraria || DEFAULT_TIMEZONE)?.toJSDate(),
        ticket.zonaHoraria || DEFAULT_TIMEZONE
      )
    : null;
  
  // Determinar el estado del evento basado en 'published'
  // published: 0=No publicado, 1=Activo, 2=Finalizado, 3=Cancelado
  const published = ticket?.published;
  const isActive = published === 1;
  const isFinalized = published === 2;
  const isCanceled = published === 3;

  // Determinar el badge a mostrar
  const getBadgeInfo = () => {
    if (isCanceled) {
      return { text: "Cancelado", bgColor: "bg-red-600" };
    }
    if (isFinalized) {
      return { text: "Finalizado", bgColor: "bg-gray-500" };
    }
    if (isActive) {
      return { text: "Activo", bgColor: "bg-aw-green" };
    }
    return { text: "No publicado", bgColor: "bg-gray-400" };
  };

  const badgeInfo = getBadgeInfo();

  return (
    <div className="min-w-[200px] max-w-[200px] bg-aw-bluedark rounded-b-xl shadow-lg overflow-hidden relative">
      {/* Badge de estado */}
      {/* <div className={`absolute top-2 right-2 z-10 ${badgeInfo.bgColor} text-white text-xs px-2 py-1 rounded-full font-semibold`}>
        {badgeInfo.text}
      </div> */}
      
      {/* Imagen del Evento */}
      <Link
        to={`/evento/${ticket.uid}`}
        onClick={scrollToTop}
        className="h-52 block overflow-hidden relative border-2 border-aw-green bg-aw-bluegray"
      >
        <img
          src={bckWoop}
          alt="Placeholder"
          className="absolute inset-0 w-2/3 h-2/3 m-auto top-0 left-0 object-cover z-0"
        />
        <LazyLoadImage
          src={ticket.img || bckWoop}
          placeholderSrc={bckWoop}
          alt={ticket.nombre}
          height={"100%"}
          width={"100%"}
          effect="blur"
          className="w-full h-full object-cover z-50"
          loading="lazy"
        />
      </Link>
      
      <div className="px-2 py-1 flex flex-col justify-between h-[150px] text-center">
        <span className="text-tiny text-white capitalize">{formatDatetime}</span>
        <h3 className="font-bold text-sm text-white line-clamp-2">{ticket.nombre}</h3>
        
        <p className="text-xs text-gray-500 line-clamp-1">
          <FontAwesomeIcon
            icon="fa-solid fa-map-pin"
            className="text-red-700 mr-1"
          />
          {ticket?.direccion}
        </p>
      </div>
      
      {/* Botón Ver Evento */}
      <Link
        to={`/evento/${ticket.uid}`}
        onClick={scrollToTop}
        className={`w-full h-9 flex items-center justify-center font-semibold text-sm mt-2 py-1 hover:scale-110 transition duration-300 ${
          isCanceled ? 'bg-gray-500 text-white' : 'bg-aw-green text-black'
        }`}
      >
        Ver Evento
        <FontAwesomeIcon
          icon="fa-solid fa-calendar-check"
          className="ml-2"
        />
      </Link>
    </div>
  );
}
